/**
 * United States Healthcare Cyber Incident Database — map/filter logic.
 * Adapted from the standalone app to run inside the WordPress theme shell
 * scoped under `.btf-cyber-incident-db`. Config (data URL, etc.) is
 * localized by PHP on `window.btfCyberIncidentDb`.
 */
(function () {
  function run() {
    const CFG = window.btfCyberIncidentDb || {};
    const ROOT = document.querySelector(".btf-cyber-incident-db");
    if (!ROOT) {
      console.error("btfCyberIncidentDb: root .btf-cyber-incident-db not found");
      return;
    }

    const ESC = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, c => ({ "&":"&amp;","<":"&lt;",">":"&gt;","\"":"&quot;","'":"&#39;" }[c]));

    function fmtNum(v){
      if (typeof v === "number") return v.toLocaleString();
      if (v == null) return "Unknown";
      const s = String(v).trim();
      if (!s || s === "Unknown" || s === "Not publicly disclosed" || s === "Not separately reported") return s || "Unknown";
      return s;
    }
    function yearOf(s){ const m=String(s||"").match(/(20\d{2}|19\d{2})/); return m?+m[1]:null; }
    function fmtDate(s){
      if (!s || s === "Unknown") return "Unknown";
      // YYYY-MM-DD
      const m1 = String(s).match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (m1) {
        const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
        return `${months[+m1[2]-1]} ${+m1[3]}, ${m1[1]}`;
      }
      return s;
    }
    function attribClass(a){ return "b-" + (["confirmed","reported","claimed","unknown"].includes(a) ? a : "unknown"); }

    let DATA = null;
    let MAP = null;
    let CLUSTER = null;
    let TILE_LAYER = null;
    let POPUP_UID = 0;

    /* ---------- Theme ---------- */
    const TILE_URLS = {
      light: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=cb1_3g9v_1_f1648633440637dd629f673f",
      dark:  "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png?key=cb1_3g9v_1_f1648633440637dd629f673f"
    };
    function getTheme(){
      return ROOT.getAttribute("data-theme") || "light";
    }
    function currentTileUrl(){
      return TILE_URLS[getTheme()] || TILE_URLS.light;
    }
    function applyTheme(theme){
      ROOT.setAttribute("data-theme", theme);
      try { localStorage.setItem("ushcid-theme", theme); } catch(e){}
      if (TILE_LAYER) TILE_LAYER.setUrl(currentTileUrl());
      const btn = document.getElementById("theme-toggle");
      if (btn) {
        const nextLabel = theme === "dark" ? "Light mode" : "Dark mode";
        btn.setAttribute("aria-label", theme === "dark" ? "Switch to light mode" : "Switch to dark mode");
        const label = btn.querySelector(".theme-label");
        if (label) label.textContent = nextLabel;
      }
    }
    (function initTheme(){
      let stored = null;
      try { stored = localStorage.getItem("ushcid-theme"); } catch(e){}
      const prefersDark = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
      ROOT.setAttribute("data-theme", stored || (prefersDark ? "dark" : "light"));
    })();

    function buildPopupHTML(org){
      const popupId = `btf-cid-popup-${++POPUP_UID}`;
      const ms = org.is_multistate ? `<div class="ms-flag" title="Multistate organization">Multistate · residents in multiple states affected</div>` : "";
      const hqOut = org.hq_outside_state ? `<div style="font-size:11px;color:#bbb6a3;margin-top:3px;font-family:'JetBrains Mono',monospace">HQ outside ${ESC(org.state || "affected state")}: ${ESC(org.hq_outside_state)}</div>` : "";
      const incidentsHTML = org.incidents.map((inc, i) => {
        const detailId = `${popupId}-incident-${i}`;
        const totalAff = fmtNum(inc.individuals_affected_reported);
        const stAff = inc.residents_affected_in_state;
        const stAffStr = (stAff == null || stAff === "Not separately reported" || stAff === "Unknown" || stAff === "Not publicly disclosed" || stAff === "") ? "Not separately reported" : fmtNum(stAff);
        const sources = [];
        if (inc.primary_source_url && /^https?:\/\//.test(inc.primary_source_url)) sources.push({label:"Primary source", url: inc.primary_source_url});
        (inc.secondary_source_urls || []).forEach(u => {
          if (u && /^https?:\/\//.test(u)) sources.push({label: hostnameOf(u), url: u});
        });
        const sourcesHTML = sources.length ? `<dt>Sources</dt><dd><ul class="src-list">${sources.map(s=>`<li><a href="${ESC(s.url)}" target="_blank" rel="noopener">${ESC(s.label)}</a></li>`).join("")}</ul></dd>` : "";
        const threat = inc.threat_actor_name && !["Unknown","Not publicly disclosed","Not separately reported",""].includes(inc.threat_actor_name) ? ESC(inc.threat_actor_name) : "Unknown / undisclosed";
        return `
          <div class="incident-card" data-i="${i}">
            <button class="incident-head" type="button" aria-expanded="false" aria-controls="${detailId}">
              <span class="left">
                <span class="date">${ESC(fmtDate(inc.disclosure_date))} · disclosed</span>
                <span class="summary-title">${ESC(inc.attack_category || "Cyber incident")}${totalAff && totalAff !== "Unknown" ? ` · ${ESC(totalAff)} affected` : ""}</span>
              </span>
              <span class="toggle" aria-hidden="true"></span>
            </button>
            <div class="incident-detail" id="${detailId}" hidden>
              <dl>
                <dd class="exec">${ESC(inc.executive_summary)}</dd>
                <dt>Discovered</dt><dd>${ESC(fmtDate(inc.discovery_date))}</dd>
                <dt>Disclosed</dt><dd>${ESC(fmtDate(inc.disclosure_date))}</dd>
                <dt>Attack type</dt><dd><span class="badge b-attack">${ESC(inc.attack_category || "Other")}</span> ${ESC(inc.attack_type)}</dd>
                <dt>Threat actor</dt><dd>${threat} <span class="badge ${attribClass(inc.attribution_status)}" title="Attribution status">${ESC(inc.attribution_status)}</span></dd>
                <dt>Affected (reported)</dt><dd>${ESC(totalAff)}</dd>
                <dt>In-state residents</dt><dd>${ESC(stAffStr)}</dd>
                <dt>Operational impact</dt><dd>${ESC(inc.operational_impact)}</dd>
                <dt>Financial impact</dt><dd>${ESC(inc.financial_impact)}</dd>
                <dt>Remediation</dt><dd>${ESC(inc.remediation_disclosed)}</dd>
                ${inc.confidence_notes ? `<dt>Notes</dt><dd style="color:var(--muted);font-size:11.5px">${ESC(inc.confidence_notes)}</dd>` : ""}
                ${sourcesHTML}
              </dl>
            </div>
          </div>`;
      }).join("");
      return `
        <div class="pop-head">
          <div class="org">${ESC(org.organization_name)}</div>
          <div class="meta">${ESC(org.organization_type_bucket)} · ${ESC(org.city || "")}${org.county && org.county !== "Unknown" && org.county !== "Multistate" ? ", " + ESC(org.county) + " County" : ""}${org.state ? ", " + ESC(org.state) : ""}${org.incidents.length>1 ? " · " + org.incidents.length + " incidents" : ""}</div>
          ${ms}
          ${hqOut}
        </div>
        <div class="pop-body">${incidentsHTML}</div>`;
    }
    function hostnameOf(u){ try{ return new URL(u).hostname.replace(/^www\./,""); } catch(e){ return u; } }

    function makeIcon(org){
      const n = org.incidents.length;
      const cls = org.is_multistate ? "multistate" : (n > 1 ? "multi" : "single");
      return L.divIcon({
        className: "",
        html: `<div class="pin ${cls}">${n>1?n:"●"}</div>`,
        iconSize: n>1?[30,30]:[22,22],
        iconAnchor: n>1?[15,15]:[11,11],
        popupAnchor: [0, -14],
      });
    }

    function bindPopupEvents(popup){
      const root = popup.getElement();
      if (!root) return;
      root.querySelectorAll(".incident-card").forEach((card, idx) => {
        const head = card.querySelector(".incident-head");
        const detail = card.querySelector(".incident-detail");
        const setOpen = (open) => {
          card.classList.toggle("open", open);
          head.setAttribute("aria-expanded", open ? "true" : "false");
          detail.hidden = !open;
        };
        setOpen(idx === 0);
        head.addEventListener("click", () => {
          setOpen(!card.classList.contains("open"));
        });
      });
    }

    function applyFilters(){
      const q = document.getElementById("search").value.trim().toLowerCase();
      const year = document.getElementById("f-year").value;
      const state = document.getElementById("f-state").value;
      const type = document.getElementById("f-type").value;
      const county = document.getElementById("f-county").value;
      const activeAttacks = [...document.querySelectorAll('#f-attack .chip.active')].map(c=>c.dataset.val);
      const activeAttribs = [...document.querySelectorAll('#f-attrib .chip.active')].map(c=>c.dataset.val);

      CLUSTER.clearLayers();
      let shownOrgs = 0, shownIncidents = 0, statesSet = new Set();
      for (const org of DATA.organizations){
        // Filter org-level
        if (state && org.state !== state) continue;
        if (type && org.organization_type_bucket !== type) continue;
        if (county && org.county !== county) continue;
        // Filter incident-level: only show org if at least one incident matches all incident-level filters
        const matchingIncidents = org.incidents.filter(inc => {
          if (year){
            const y = yearOf(inc.disclosure_date) || yearOf(inc.discovery_date);
            if (String(y) !== year) return false;
          }
          if (activeAttacks.length && !activeAttacks.includes(inc.attack_category)) return false;
          if (activeAttribs.length && !activeAttribs.includes(inc.attribution_status)) return false;
          if (q){
            const blob = (org.organization_name + " " + (inc.executive_summary||"") + " " + (inc.threat_actor_name||"") + " " + (inc.attack_type||"")).toLowerCase();
            if (!blob.includes(q)) return false;
          }
          return true;
        });
        if (!matchingIncidents.length) continue;

        // Build a temporary filtered org for popup display
        const filteredOrg = {...org, incidents: matchingIncidents};
        const m = L.marker([org.lat, org.lng], {icon: makeIcon(filteredOrg)});
        m.bindPopup(buildPopupHTML(filteredOrg), {maxWidth: 380, autoPan: true});
        m.on("popupopen", e => bindPopupEvents(e.popup));
        CLUSTER.addLayer(m);
        shownOrgs++;
        shownIncidents += matchingIncidents.length;
        if (org.state) statesSet.add(org.state);
      }
      document.getElementById("stats").textContent = `${shownIncidents.toLocaleString()} incidents · ${shownOrgs.toLocaleString()} orgs · ${statesSet.size} states shown`;
    }

    function initFilters(){
      const yearSel = document.getElementById("f-year");
      DATA.facets.years.slice().reverse().forEach(y => { const o = document.createElement("option"); o.value = y; o.textContent = y; yearSel.appendChild(o); });

      const stateSel = document.getElementById("f-state");
      (DATA.facets.states || []).forEach(s => { const o = document.createElement("option"); o.value = s; o.textContent = s; stateSel.appendChild(o); });

      const typeSel = document.getElementById("f-type");
      DATA.facets.org_types.forEach(t => { const o = document.createElement("option"); o.value = t; o.textContent = t; typeSel.appendChild(o); });

      const countySel = document.getElementById("f-county");
      DATA.facets.counties.forEach(c => { const o = document.createElement("option"); o.value = c; o.textContent = c; countySel.appendChild(o); });

      const attackWrap = document.getElementById("f-attack");
      DATA.facets.attack_types.forEach(t => {
        const button = document.createElement("button");
        button.className = "chip";
        button.type = "button";
        button.setAttribute("type", "button");
        button.setAttribute("aria-pressed", "false");
        button.dataset.val = t;
        button.textContent = t;
        attackWrap.appendChild(button);
      });

      // Toggle behavior for chips
      document.querySelectorAll('.chip-row .chip').forEach(c => c.addEventListener("click", () => {
        const active = c.classList.toggle("active");
        c.setAttribute("aria-pressed", active ? "true" : "false");
        applyFilters();
      }));

      // Selects + search
      ["f-year","f-state","f-type","f-county"].forEach(id => document.getElementById(id).addEventListener("change", applyFilters));
      let t;
      document.getElementById("search").addEventListener("input", () => { clearTimeout(t); t=setTimeout(applyFilters, 150); });

      document.getElementById("reset").addEventListener("click", () => {
        document.getElementById("search").value = "";
        ["f-year","f-state","f-type","f-county"].forEach(id => document.getElementById(id).value = "");
        document.querySelectorAll('.chip.active').forEach(c => {
          c.classList.remove("active");
          c.setAttribute("aria-pressed", "false");
        });
        applyFilters();
      });

      // Theme toggle
      const themeBtn = document.getElementById("theme-toggle");
      if (themeBtn){
        themeBtn.addEventListener("click", () => {
          const next = getTheme() === "dark" ? "light" : "dark";
          applyTheme(next);
          setToolsMenuOpen(false);
        });
        applyTheme(getTheme());
      }
    }

    function setToolsMenuOpen(open){
      const toggle = document.getElementById("tools-toggle");
      const popover = document.getElementById("tools-popover");
      if (!toggle || !popover) return;
      toggle.setAttribute("aria-expanded", open ? "true" : "false");
      toggle.setAttribute("aria-label", open ? "Close tools menu" : "Open tools menu");
      if (open) popover.removeAttribute("hidden");
      else popover.setAttribute("hidden", "");
    }

    function initToolsMenu(){
      const toggle = document.getElementById("tools-toggle");
      const menu = document.getElementById("tools-menu");
      const popover = document.getElementById("tools-popover");
      if (!toggle || !menu || !popover) return;

      setToolsMenuOpen(false);

      toggle.addEventListener("click", (e) => {
        e.stopPropagation();
        const open = toggle.getAttribute("aria-expanded") !== "true";
        setToolsMenuOpen(open);
      });

      document.addEventListener("click", (e) => {
        const target = e.target;
        if (!target) return;
        if (typeof menu.contains === "function" && menu.contains(target)) return;
        if (target === menu || target === toggle || target === popover) return;
        setToolsMenuOpen(false);
      });

      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && toggle.getAttribute("aria-expanded") === "true") {
          setToolsMenuOpen(false);
          toggle.focus();
        }
      });
    }

    function initDrawer(){
      const filtersBtn = document.getElementById("filters-toggle");
      const sidebar = document.getElementById("sidebar");
      const scrim = document.getElementById("scrim");
      const closeBtn = document.getElementById("sidebar-close");
      const mobileQuery = window.matchMedia("(max-width: 860px)");
      const isMobile = () => mobileQuery.matches;
      function setDrawer(open){
        const mobile = isMobile();
        const wasOpen = ROOT.getAttribute("data-drawer") === "open";
        const shouldOpen = open && mobile;
        if (shouldOpen) ROOT.setAttribute("data-drawer", "open");
        else ROOT.removeAttribute("data-drawer");
        if (filtersBtn) {
          filtersBtn.setAttribute("aria-expanded", shouldOpen ? "true" : "false");
          filtersBtn.setAttribute("aria-label", shouldOpen ? "Close filters" : "Open filters");
        }
        if (sidebar) {
          const hidden = mobile && !shouldOpen;
          sidebar.setAttribute("aria-hidden", hidden ? "true" : "false");
          sidebar.inert = hidden;
        }
        if (shouldOpen && closeBtn) closeBtn.focus();
        else if (wasOpen && filtersBtn) filtersBtn.focus();
        // Notify Leaflet that container size may have changed
        setTimeout(() => { if (MAP) MAP.invalidateSize(); }, 280);
      }
      if (filtersBtn) filtersBtn.addEventListener("click", () => setDrawer(ROOT.getAttribute("data-drawer") !== "open"));
      if (closeBtn) closeBtn.addEventListener("click", () => setDrawer(false));
      if (scrim) scrim.addEventListener("click", () => setDrawer(false));
      document.addEventListener("keydown", (e) => { if (e.key === "Escape" && ROOT.getAttribute("data-drawer") === "open") setDrawer(false); });
      setDrawer(false);

      if (mobileQuery.addEventListener) {
        mobileQuery.addEventListener("change", () => setDrawer(false));
      }
      window.addEventListener("resize", () => {
        if (MAP) MAP.invalidateSize();
      });
    }

    async function init(){
      const url = CFG.dataUrl;
      if (!url) throw new Error("Missing dataUrl");
      const res = await fetch(url);
      if (!res.ok) throw new Error("HTTP " + res.status);
      DATA = await res.json();

      MAP = L.map("map", {zoomControl: true, preferCanvas: true}).setView([39.5, -98.5], 4);
      TILE_LAYER = L.tileLayer(currentTileUrl(), {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors · © <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: "abcd",
        maxZoom: 18,
      }).addTo(MAP);

      CLUSTER = L.markerClusterGroup({
        showCoverageOnHover: false,
        spiderfyOnMaxZoom: true,
        maxClusterRadius: 45,
        iconCreateFunction: (cluster) => {
          const n = cluster.getChildCount();
          const cls = n>=20 ? "marker-cluster-large" : n>=10 ? "marker-cluster-medium" : "marker-cluster-small";
          return L.divIcon({html:`<div><span>${n}</span></div>`, className:`marker-cluster ${cls}`, iconSize:L.point(40,40)});
        },
      });
      MAP.addLayer(CLUSTER);

      initFilters();
      applyFilters();

      MAP.invalidateSize();
    }
    initDrawer();
    initToolsMenu();
    init().catch(err => {
      const mapEl = document.getElementById("map");
      if (mapEl) {
        mapEl.innerHTML = `<div class="empty">Incident data could not be loaded.</div>`;
      }
      console.error(err);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run);
  } else {
    run();
  }
})();
